
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
// Shared CanvasElement type for use across components
export interface CanvasElement {
  id: string;
  type: 'character' | 'scene' | 'event' | 'worldbuilding' | 'chatbox';
  x: number;
  y: number;
  width: number;
  height: number;
  title: string;
  content: string;
  color: string;
  connections: string[];
  avatar?: string;
  imageUrl?: string;
  personality?: string;
  background?: string;
  attributes?: { [key: string]: any };
  isSelected?: boolean;
  groupId?: string;
  dialogues?: Array<{
    character?: string;
    content: string;
    type: 'dialogue' | 'narration' | 'thought';
  }>;
}

export interface CanvasMetadata {
  worldview?: string;
  backgroundStory?: string;
  theme?: string;
  timeperiod?: string;
  genre?: string;
  tone?: string;
  [key: string]: any;
}

interface CanvasMetadataManagerProps {
  metadata: CanvasMetadata;
  onMetadataUpdate: (metadata: CanvasMetadata) => void;
  isVisible: boolean;
  onClose: () => void;
}

const CanvasMetadataManager: React.FC<CanvasMetadataManagerProps> = ({
  metadata,
  onMetadataUpdate,
  isVisible,
  onClose
}) => {
  const { t } = useTranslation();
  const [editingMetadata, setEditingMetadata] = useState<CanvasMetadata>(metadata);

  const handleInputChange = (field: string, value: string) => {
    setEditingMetadata(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    onMetadataUpdate(editingMetadata);
    onClose();
  };

  const handleCancel = () => {
    setEditingMetadata(metadata);
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#1a1a2e',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '12px',
          padding: '24px',
          width: '600px',
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflowY: 'auto',
          color: '#ffffff'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginBottom: '24px', color: '#4cc9f0' }}>
          {t('canvas.metadataManager.title')}
        </h2>
        
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            {t('canvas.metadataManager.worldview')}
          </label>
          <textarea
            value={editingMetadata.worldview || ''}
            onChange={(e) => handleInputChange('worldview', e.target.value)}
            placeholder={t('canvas.metadataManager.worldviewPlaceholder')}
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '12px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            {t('canvas.metadataManager.backgroundStory')}
          </label>
          <textarea
            value={editingMetadata.backgroundStory || ''}
            onChange={(e) => handleInputChange('backgroundStory', e.target.value)}
            placeholder={t('canvas.metadataManager.backgroundStoryPlaceholder')}
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '12px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              {t('canvas.metadataManager.theme')}
            </label>
            <input
              type="text"
              value={editingMetadata.theme || ''}
              onChange={(e) => handleInputChange('theme', e.target.value)}
              placeholder={t('canvas.metadataManager.themePlaceholder')}
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              {t('canvas.metadataManager.timeperiod')}
            </label>
            <input
              type="text"
              value={editingMetadata.timeperiod || ''}
              onChange={(e) => handleInputChange('timeperiod', e.target.value)}
              placeholder={t('canvas.metadataManager.timeperiodPlaceholder')}
              style={{
                width: '100%',
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '14px'
              }}
            />
          </div>
        </div>



        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={handleCancel}
            style={{
              padding: '12px 24px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: '#ffffff',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {t('canvas.metadataManager.cancel')}
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '12px 24px',
              background: '#4cc9f0',
              border: 'none',
              borderRadius: '8px',
              color: '#1a1a2e',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {t('canvas.metadataManager.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CanvasMetadataManager;